import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import useValidatedInputEffect from "../../../hooks/useValidatedInputEffect";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectRooms, selectRoomsRestSet } from "../../../store/room/room-selectors";
import ValidationResult from "../../../interfaces/api/validation-result";
import useServerValidationResponseEffect from "../../../hooks/useServerValidationResponseEffect";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import TextInput from "../../Inputs/TextInput";

const RestoreRoomDialog: React.FC<{
  isOpen: boolean;
  roomMetaInfo: RoomData;
  onClose: () => void;
  onSave: () => void;
}> = ({ isOpen, roomMetaInfo, onClose, onSave }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const availableRooms = useSharedSelector((state) => selectRooms(state));
  const roomsRestSet = useSharedSelector(state => selectRoomsRestSet(state));

  const {postRestoreRoomById, patchRoomsRestSet} = useSharedActions();

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    postError: roomsRestSet.postError,
    resetPatchHandler: () =>
      
      dispatch(patchRoomsRestSet({ postStatus: HttpFetchStatus.Idle, postError: null })),
  });

  const {
    value: roomName,
    validationResult: roomNameValidationResult,
    valueChangeHandler: roomNameChangeHandler,
  } = useValidatedInputEffect(
    roomMetaInfo.name ?? "",
    (_) => ({ isValid: true, error: null } as ValidationResult),
    (_) => () => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle
  );

  const {
    value: stationId,
    validationResult: stationIdValidationResult,
    valueChangeHandler: stationIdChangeHandler,
  } = useValidatedInputEffect(
    roomMetaInfo.primaryStation?.stationId ?? "",
    (_) => ({ isValid: true, error: null } as ValidationResult),
    (_) => () => {},
    getServerValidationResultOrNull("stationId"),
    removeServerError,
    resetToggle
  );

  const restoreRoomClickHandler = () => {
    onSave();
    dispatch(postRestoreRoomById({ roomId: roomMetaInfo.id, roomName, stationId }));
  };

  const isConfirmEnabled = stationId.length > 0 && roomName.length > 0;

  return (
    <React.Fragment>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("rooms-restore-room")}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {availableRooms.some((r) => r.id === roomMetaInfo.id) && (
              <Alert severity="warning">{t("rooms-restore-room-already-exists-warning")}</Alert>
            )}
            {availableRooms.some(
              (r) => r.primaryStation.stationId === stationId && r.isArchived === false && r.id !== roomMetaInfo.id
            ) && <Alert severity="warning">{t("rooms-restore-room-at-station-exists-warning")}</Alert>}

            <TextInput
              label={t("rooms-restore-label-room-name")}
              value={roomName}
              onChange={roomNameChangeHandler}
              error={!roomNameValidationResult.isValid}
              helperText={roomNameValidationResult.error}
              fullWidth
              variant="standard"
              data-testid="input-room-name-id"
            />
            <TextInput
              label={t("rooms-restore-label-station-id")}
              value={stationId}
              onChange={stationIdChangeHandler}
              error={!stationIdValidationResult.isValid}
              helperText={stationIdValidationResult.error}
              fullWidth
              data-testid="input-station-id-id"
              variant="standard"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" data-testid="button-abort-id">
            {t("button-abort")}
          </Button>
          <Button
            onClick={restoreRoomClickHandler}
            color="primary"
            autoFocus
            disabled={!isConfirmEnabled}
            data-testid="button-confirm-id"
          >
            {t("button-confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default RestoreRoomDialog;
